import { useMemo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { Link } from '~/components/link';
import { trackEvent } from '~/features/analytics/track-event';
import { isMarketingRoute } from '~/features/environment/one-trust-gating';
import { getViewerCountryCode, getViewerRegionCode } from '~/features/internationalization/utils/get-viewer-geo-codes';
import { isEuCountryCode } from '~/features/internationalization/utils/is-eu-country-code';
import { useBaseUrl } from '~/hooks/use-base-url';
import { useClientRender } from '~/hooks/use-client-render';

import { getLegalNavigation, getLegalText } from '../data';

export function Legal() {
  return (
    <Flex
      borderTop={{ base: 'none', md: '1px solid' }}
      borderTopColor='whiteAlpha.400 !'
      flexDirection={{ base: 'column', md: 'row' }}
      gap={{ base: '20px', md: '40px' }}
      justifyContent='space-between'
      mt={{ base: '0', md: '48px' }}
      pb={{ base: '24px', md: '32px' }}
      pt={{ base: '24px', md: '32px' }}
    >
      <Flex
        alignItems={{ base: 'flex-start', md: 'center' }}
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: '20px', md: '40px' }}
      >
        <FooterLogo />
        <LegalText />
      </Flex>
      <LegalNavigation />
    </Flex>
  );
}

function FooterLogo() {
  const baseUrl = useBaseUrl();

  return (
    <Link aria-label='Link to home page' display='flex' href={baseUrl} locale={false} width='fit-content'>
      <Image alt='Udacity Accenture logo' height={32} src='/images/logos/udacity-accenture.svg' width={135} />
    </Link>
  );
}

export function LegalText() {
  const { t } = useTranslation();
  const legalText = useMemo(() => getLegalText(t), [t]);

  return (
    <Box color='gray.300' maxW={{ base: '100%', md: '640px' }}>
      {legalText}
    </Box>
  );
}

export function LegalNavigation() {
  const clientReady = useClientRender();
  const baseUrl = useBaseUrl();
  const { t } = useTranslation();
  const legalNavigation = useMemo(() => getLegalNavigation(t), [t]);
  // OneTrust is only injected on marketing routes (see `_document.tsx`), so the
  // "Cookie Settings" control would be a silent no-op elsewhere. Gate it to the
  // same set of surfaces to avoid showing an inert button on product pages.
  const showCookieSettings = clientReady && typeof window !== 'undefined' && isMarketingRoute(window.location.pathname);

  return (
    <Box
      aria-label='Legal & privacy related links.'
      as='nav'
      columnGap='12px'
      display='flex'
      flexWrap='wrap'
      justifyContent={{ base: 'flex-start', md: 'flex-end' }}
      rowGap='8px'
    >
      {legalNavigation.map((link, index) => (
        <Flex key={link.href} alignItems='center' gap='12px'>
          {index > 0 && <Text color='gray.200'>·</Text>}
          <Link
            _focusVisible={{ outline: '2px solid', outlineColor: 'blue.200', outlineOffset: '2px' }}
            _hover={{ color: 'blue.200', textDecoration: 'underline' }}
            _visited={{ color: 'gray.200' }}
            color='gray.200'
            fontSize={{ base: '12px', md: '13px' }}
            fontWeight='400'
            href={link.isAbsolute ? link.href : `${baseUrl}${link.href}`}
            isExternal={link.isExternal}
            locale={false}
            textDecoration='none'
          >
            {link.label}
          </Link>
        </Flex>
      ))}
      {showCookieSettings && (
        <Flex alignItems='center' gap='12px'>
          <Text color='gray.200'>·</Text>
          <Button
            _focusVisible={{ outline: '2px solid', outlineColor: 'blue.200', outlineOffset: '2px' }}
            _hover={{ color: 'blue.200', textDecoration: 'underline' }}
            color='gray.200'
            fontSize={{ base: '12px', md: '13px' }}
            fontWeight='400'
            textDecoration='none'
            variant='link'
            onClick={() => {
              if ('OneTrust' in window) {
                (window as any)?.OneTrust?.ToggleInfoDisplay?.();
              }
            }}
          >
            Cookie Settings
          </Button>
        </Flex>
      )}
      {clientReady && <DoNotSellMyDataButton />}
    </Box>
  );
}

function DoNotSellMyDataButton() {
  const { t } = useTranslation();

  const countryCode = getViewerCountryCode();
  const regionCode = getViewerRegionCode();
  const US_STATES_REQUIRING_DO_NOT_SELL = ['CA', 'NE', 'TX'] as const;
  const isInRequiredUSState =
    countryCode === 'US' &&
    regionCode &&
    US_STATES_REQUIRING_DO_NOT_SELL.includes(regionCode as (typeof US_STATES_REQUIRING_DO_NOT_SELL)[number]);

  function doNotSellMyDataClickHandler() {
    trackEvent({
      name: 'CCPA CTA Clicked',
      type: 'click',
      label: 'Do Not Sell My Data',
      action: 'ccpa_cta_clicked',
    });
  }

  if (!isInRequiredUSState && !isEuCountryCode(countryCode!)) return null;

  return (
    <Flex alignItems='center' gap='12px'>
      <Text color='gray.200'>·</Text>
      <ButtonLink
        buttonProps={{
          _focusVisible: { outline: '2px solid', outlineColor: 'blue.200', outlineOffset: '2px' },
          _hover: { color: 'blue.200', textDecoration: 'underline' },
          color: 'gray.200',
          fontSize: { base: '12px', md: '13px' },
          fontWeight: '400',
          textDecoration: 'none',
          variant: 'link',
          onClick: doNotSellMyDataClickHandler,
        }}
        linkProps={{
          href: 'https://www.accenture.com/us-en/support/form-do-not-sell-my-personal-information',
        }}
        isExternal
      >
        {t('footer.doNotSellMyData')}
      </ButtonLink>
    </Flex>
  );
}
